import React from 'react';
import styled, { keyframes } from 'styled-components';
import { system, extractSysProps } from './system';

export const spinKeyframes = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Local alias so existing internal references to `spin` continue to work.
const spin = spinKeyframes;

// System prop keys consumed by Spinner (not forwarded to DOM).
// Intentionally narrower than the full SYS_KEYS_SET — molekule's Spinner
// composed fewer utilities than Box.
const SPINNER_SYS_KEYS: ReadonlySet<string> = new Set([
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
  'display',
  'alignSelf',
]);

interface StyledSpinnerProps {
  $size: number;
  $sys?: Record<string, any>;
}

const StyledSpinner = styled.div<StyledSpinnerProps>`
  height: ${(p) => p.$size}px;
  width: ${(p) => p.$size}px;
  border: ${(p) => Math.max(1, Math.round(p.$size / 10))}px solid
    ${(p) =>
      p.theme && p.theme.colors && p.theme.colors.primary
        ? p.theme.colors.primary + '4D' /* ~30% opacity */
        : 'rgba(255, 111, 97, 0.3)'};
  border-right-color: ${(p) =>
    p.theme && p.theme.colors && p.theme.colors.primary
      ? p.theme.colors.primary
      : '#FF6F61'};
  border-radius: 50%;
  animation: ${spin} 1.1s infinite linear;
  ${(p) => system(p.$sys, p.theme)}
`;

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  size?: number;
  [key: string]: any;
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  function Spinner(props, ref) {
    const { size = 15, ...remaining } = props;
    const { sys, rest } = extractSysProps(remaining as Record<string, any>, SPINNER_SYS_KEYS);
    return <StyledSpinner ref={ref} $size={size} $sys={sys} {...rest} />;
  },
);

export default Spinner;
